import {
	ChangeEvent,
	ReactElement,
	useLayoutEffect,
	useRef,
	useState,
	KeyboardEvent,
	useMemo,
	useCallback,
	useEffect,
	useContext,
	forwardRef,
	Ref,
	useImperativeHandle,
	FocusEvent,
	RefObject
} from "react";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { tryResolveKeyboardKey } from "client/Misc/keyboardUtils";
import { Icon } from "../Icon/Icon";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import classNames from "classnames";
import {
	generateTrackingId,
	setTrackingId,
	debounce
} from "client/Misc/utils";
import {
	ControlAttachmentContext,
	ControlAttachmentOptions,
	getAttachmentClassNames
} from "../ControlAttachment";
import styles from "./SearchInput.module.less";
import { Key } from "client/Misc/commonTypes";
import { IconButton } from "../IconButton/IconButton";
import { KeyEventArguments } from "../TextInput/TextInput";

export type SearchInputTrigger = {
	kind: ChangeTriggerKind.Instant;
} | {
	kind: ChangeTriggerKind.OnEnter;
} | {
	kind: ChangeTriggerKind.Debounced;
	debounceInMs?: number;
};

export type SearchInputProps = {
	value?: VersionedValue<string>;
	onChange(value: string, version: Guid): void;
	trigger?: SearchInputTrigger;
	placeholder?: string | ReactElement;
	icon?: "magnifying-glass" | "filter" | "none";
	height?: SearchInputHeight;
	isBorderless?: boolean;
	trackingId?: string;
	attachedOn?: ControlAttachmentOptions;
	onNavigateToResults?(): void;
	autoFocus?: boolean;
	onFocus?(value: string): void;
	variant?: "default" | "white";
	isDisabled?: boolean;
	onKeyDown?: (args: KeyEventArguments) => boolean;
};

export type SearchInputHandle = {
	focus(): void;
	blur(): void;
};

export const SearchInput = forwardRef((props: SearchInputProps, ref: Ref<SearchInputHandle>): ReactElement => {
	const {
		value,
		trigger = { kind: ChangeTriggerKind.Instant as const },
		placeholder,
		icon = "magnifying-glass",
		onChange,
		height = "auto",
		isBorderless = false,
		trackingId,
		attachedOn = [],
		onNavigateToResults,
		autoFocus = false,
		onFocus,
		variant = "default",
		isDisabled= false,
		onKeyDown
	} = props;

	const [shouldFocusInput, setShouldFocusInput] = useState<boolean>(false);
	const [internalValue, setInternalValue] = useState<string>(value?.value ?? "");
	const lastVersionIssued: RefObject<Guid | undefined> = useRef<Guid>(value?.version);
	const preventDebounce: RefObject<boolean> = useRef<boolean>(false);
	const inputRef: RefObject<HTMLInputElement | null> = useRef<HTMLInputElement>(null);

	const {
		attachedOn: attachedOnFromContext
	} = useContext(ControlAttachmentContext);

	const fireOnChange = useCallback(
		(value: string): void => {
			const version: Guid = newGuid();
			lastVersionIssued.current = version;
			onChange(value, version);
		},
		[onChange]
	);

	useImperativeHandle(
		ref,
		() => ({
			focus: () => inputRef.current?.focus(),
			blur: () => inputRef.current?.blur()
		})
	);

	const debouncedFireOnChange: OptionalModel<(value: string) => void> = useMemo(
		() => {
			if (trigger.kind === ChangeTriggerKind.Debounced) {
				const {
					debounceInMs = 250
				} = trigger;

				if (debounceInMs <= 0) {
					throw new Error("Debounce must be a positive number");
				}

				const debouncedInvocation = debounce(
					(x: string): void => {
						if (preventDebounce.current) {
							preventDebounce.current = false;
							return;
						}
						fireOnChange(x);
					},
					debounceInMs
				);

				return {
					isSet: true,
					value: (value: string) => {
						preventDebounce.current = false;
						debouncedInvocation(value);
					}
				};
			} else {
				return { isSet	: false};
			}
		},
		[fireOnChange, trigger]
	);

	useEffect(
		() => {
			if (!value) {
				return;
			}

			if (value.version !== lastVersionIssued.current) {
				lastVersionIssued.current = value.version;
				setInternalValue(value.value ?? "");
				preventDebounce.current = true;
			}
		},
		[value]
	);

	const clear = (): void => {
		setValue("", true);
		setShouldFocusInput(true);
	};

	const setValue = (value: string, invokedFromClear: boolean = false): void => {
		setInternalValue(value);

		switch (trigger.kind)
		{
			case ChangeTriggerKind.Instant:
				fireOnChange(value);
				break;
			case ChangeTriggerKind.Debounced:
				if (!debouncedFireOnChange.isSet) {
					throw new Error("The debounced on change is not set");
				}
				debouncedFireOnChange.value(value);
				break;
			case ChangeTriggerKind.OnEnter:
				if (invokedFromClear) {
					fireOnChange(value);
					return;
				}
				break;
			default:
				throw new UnreachableCaseError(trigger);
		}
	};

	const onValueChange = (args: ChangeEvent<HTMLInputElement>): void => {
		const newValue: string = args.target.value;
		setValue(newValue);
	};

	const keyDownHandler = (args: KeyboardEvent<HTMLInputElement>): void => {
		const key: Key | null = tryResolveKeyboardKey(args);

		if (onKeyDown) {
			const result: boolean = onKeyDown({
				key,
				modifiers: {
					shift: args.shiftKey,
					capsLock: args.getModifierState("CapsLock")
				}
			});

			if (result) {
				args.preventDefault();
				return;
			}
		}

		switch (key)
		{
			case Key.Enter:
				if (trigger.kind === ChangeTriggerKind.OnEnter) {
					fireOnChange(internalValue);
				}
				break;
			case Key.ArrowDown:
				if (onNavigateToResults) {
					args.preventDefault();
					onNavigateToResults();
				}
				break;
			default:
				// Nothing to do
				break;
		}
	};

	const focusHandler = useCallback(
		(args: FocusEvent<HTMLInputElement>): void => {
			if (!onFocus) {
				return;
			}

			const newValue: string = args.target.value;
			onFocus(newValue);
		},
		[onFocus]
	);

	useLayoutEffect(
		() => {
			if (inputRef.current && shouldFocusInput) {
				inputRef.current.focus();
				setShouldFocusInput(false);
			}
		},
		[shouldFocusInput]
	);

	return (
		<div
			className={classNames(
				styles.root,
				heightToStyleMap[height],
				{
					[styles.root_disabled]: isDisabled
				}
			)}
		>
			{icon !== "none" && (
				<div className={styles.icon}>
					<Icon value={icon} />
				</div>
			)}
			<input
				{...setTrackingId(trackingId)}
				type="search"
				className={classNames(
					styles.input,
					{
						[styles.input_borderless]: isBorderless,
						[styles.input_white]: variant === "white",
						[styles.input_withoutIcon]: icon === "none"
					},
					getAttachmentClassNames(attachedOn, attachedOnFromContext)
				)}
				value={internalValue}
				onChange={onValueChange}
				ref={inputRef}
				onKeyDown={keyDownHandler}
				autoFocus={autoFocus}
				enterKeyHint="search"
				onFocus={focusHandler}
				disabled={isDisabled}
			/>
			{(!internalValue && placeholder) && (
				<div className={
					classNames(
						styles.placeholder,
						{
							[styles.placeholder_withoutIcon]: icon === "none"
						}
					)
				}>
					{placeholder}
				</div>
			)}
			{ internalValue && (
				<div className={styles.clear}>
					<IconButton
						icon="xmark"
						kind="transparent-colored"
						trackingId={generateTrackingId(trackingId, "__clear")}
						onClick={clear}
						height="fill"
					/>
				</div>
			)}
		</div>
	);
});

SearchInput.displayName = "SearchInput";

export const enum ChangeTriggerKind {
	Instant = 0,
	Debounced = 1,
	OnEnter = 2
}

type SearchInputHeight = "auto" | "fill" | "large";

const heightToStyleMap: Record<SearchInputHeight, string | null> = {
	"auto": null,
	"fill": styles.root_fillHeight,
	"large": styles.root_large
};